import { useState, useRef, useEffect } from 'react';

/**
 * RichSelect - Dropdown with label + description per option.
 *
 * @param {string} label - Field label
 * @param {string} value - Selected value
 * @param {function} onChange - Callback with selected value
 * @param {Array<{value, label, desc}>} options - Options list
 * @param {string} placeholder - Placeholder text
 * @param {boolean} disabled - Disabled state
 */
export default function RichSelect({ label, value, onChange, options, placeholder = '-- Chọn --', disabled = false }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const selected = options.find(o => o.value === value);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    return (
        <div className="relative" ref={ref}>
            {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen(!open)}
                className="w-full text-left bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed relative pr-8"
            >
                {selected ? (
                    <span className="font-medium text-gray-800">{selected.label}</span>
                ) : (
                    <span className="text-gray-400">{placeholder}</span>
                )}
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={open ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} />
                    </svg>
                </span>
            </button>
            {open && (
                <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                    {options.map(opt => (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => { onChange(opt.value); setOpen(false); }}
                            className={`w-full text-left px-3 py-2.5 hover:bg-blue-50 transition-colors border-b border-gray-50 last:border-0 ${opt.value === value ? 'bg-blue-50' : ''}`}
                        >
                            <div className="font-medium text-sm text-gray-800">{opt.label}</div>
                            {opt.desc && <div className="text-xs text-gray-500 mt-0.5">{opt.desc}</div>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
